import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { isImageRatioGreaterSixteenNine, currencyFormat } from 'utils';
import { Flex } from 'components/common/styled/common-styled';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import { formatEventDateShort } from 'utils/dateTime';
import { ExclusiveTagOverlay } from 'components/common/ExclusiveTagOverlay';
import { NormalizedPublicListing } from 'types/listingV3';
import { formatListingMedia } from 'utils/listings';
import { useIsMobile } from 'WindowDimensionProvider';
import { OldLandingPageImage } from 'components/molecules/OldLandingPageImage/OldLandingPageImage';
import {
  applyMobileFontSize,
  FontSizeProps,
} from '../theme/Typography/ResourceTypography';

interface Props {
  resourceGroupCollectionId: string;
  medias: NormalizedPublicListing['medias'];
  title: string;
  dates: string[];
  prices: number[];
  exclusiveTagName: string | null;
}

const EventItem: FC<Props> = ({
  resourceGroupCollectionId,
  medias,
  title,
  dates,
  prices,
  exclusiveTagName,
}) => {
  const history = useHistory();
  const isMobile = useIsMobile();
  const currency = useBrandCurrency();
  const [isHovered, setIsHovered] = useState(false);

  const [isSixteenNineRatio, setIsSixteenNineRatio] = useState(false);

  const formattedMedias = formatListingMedia(medias);
  const coverMedia = formattedMedias.find((media) => media.kind === 'cover');

  const onImgLoadSixteenNineRatio = (
    e: React.SyntheticEvent<HTMLImageElement>,
  ) => {
    if (e.target instanceof HTMLImageElement) {
      setIsSixteenNineRatio(
        isImageRatioGreaterSixteenNine(
          e.target.naturalWidth,
          e.target.naturalHeight,
        ),
      );
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      history.push(`/event/${resourceGroupCollectionId}`);
    }
  };

  return (
    <CollectionViewItem
      onClick={() => {
        history.push(`/event/${resourceGroupCollectionId}`);
      }}
      tabIndex={0}
      role="button"
      onKeyDown={handleKeyDown}
      onMouseOver={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardGroup className="collection-item__card-group">
        <CardImage className="collection-item__card-image">
          {exclusiveTagName && isHovered ? (
            <ExclusiveTagOverlay>{exclusiveTagName}</ExclusiveTagOverlay>
          ) : null}
          <CardImageRatio className="collection-item__card-image-ratio">
            <OldLandingPageImage
              src={isMobile ? coverMedia?.urlMobile : coverMedia?.urlDesktop}
              loading="lazy"
              alt={title}
              onLoad={onImgLoadSixteenNineRatio}
              isSixteenNineRatio={isSixteenNineRatio}
            />
          </CardImageRatio>
        </CardImage>
        <Flex
          direction="column"
          alignItem="flex-start"
          justifyContent="flex-start"
          gap={12}
        >
          <Title mobileFontSize="16px" className="collection-item__title">
            {title}
          </Title>
          {dates[0] && (
            <DateLabel className="collection-item_date-label">
              Next on {formatEventDateShort(dates[0])}
            </DateLabel>
          )}
          {prices[0] >= 0 && (
            <PriceLabel className="collection-item_price-label">
              {prices.length === 1 && prices[0] === 0
                ? 'Complimentary'
                : `${currencyFormat(currency)(Math.min(...prices))}${
                    prices[1] >= 0
                      ? ` - ${currencyFormat(currency)(Math.max(...prices))}`
                      : ''
                  }`}
            </PriceLabel>
          )}
        </Flex>
      </CardGroup>
    </CollectionViewItem>
  );
};

const CollectionViewItem = styled.div`
  cursor: pointer;
  max-width: calc(100vw - 24px);
`;

const CardGroup = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CardImage = styled.div`
  margin-bottom: 20px;
  border-radius: var(--way-design-borderRadiusDefault);
  background: inherit;
  position: relative;
`;

const CardImageRatio = styled.div`
  padding-bottom: 56.25%;
  overflow: hidden;
  position: relative;
`;

const Title = styled.h3<FontSizeProps>`
  font-size: 20px;
  font-style: normal;
  font-weight: 350;
  line-height: 24px;
  margin: 0;
  color: var(--way-colors-primaryTextColor);

  ${(props) => applyMobileFontSize(props)};
`;

const DateLabel = styled.span`
  font-size: 16px;
  font-weight: 350;
  line-height: 16px;
  color: var(--way-colors-secondaryTextColor);
`;

const PriceLabel = styled.span`
  font-size: 13px;
  font-weight: 350;
  line-height: 16px;
  color: var(--way-colors-secondaryTextColor);
`;

export default EventItem;
